import type { CardFaces } from "./card-faces-chunk";

/**
 * THE CLASSIC SVG CARD FACES, LOADED OFF THE BOOT PATH.
 *
 * `@letele/playing-cards` is **34.6% of the entire JS bundle** (478,682 minified / 155,354
 * gzipped, measured from the sourcemap on 2026-08-03) and it is needed by exactly five screens —
 * the card tables. Keeping it in the entry chunk put it on the ONE fetch a user cannot recover
 * from: on the Iranian mobile paths this app mostly serves, large HTTPS responses are severed
 * mid-transfer (12.2% of bundle fetches that day), and a severed entry chunk is a blank screen
 * with no app to retry with and no beacon to report it. Deferring it moves those bytes onto a
 * fetch that CAN be retried while the app is running.
 *
 * ⚠️⚠️ THE RETRY CANNOT REUSE THE SAME SPECIFIER — MEASURED IN A REAL BROWSER, NOT ASSUMED.
 * Chromium caches the REJECTION in its module registry: four `import("/chunk.js")` calls after a
 * severed response produced **one** network request, and every later call failed instantly
 * without re-fetching — even once the server was serving the file perfectly. A retry loop around
 * a plain `import()` is a loop that never retries. Varying the specifier
 * (`import("/chunk.js?retry=1")`) produced **four** requests and succeeded on the attempt where
 * the bytes arrived intact. Hence: attempt 0 is the ordinary Vite dynamic import (so the happy
 * path stays entirely standard), and every retry goes through the chunk's real URL — read from
 * Vite's build manifest — with a distinct query.
 */

/** Where Vite writes the manifest (`build.manifest: true`), relative to the site root. */
const MANIFEST_URL = "/.vite/manifest.json";
/** The manifest key for the chunk — the source path, relative to the Vite root (`apps/web`). */
const CHUNK_SOURCE = "src/game/card-faces-chunk.ts";
/** Attempt 0 is the plain import; this bounds the RETRIES after it. */
const MAX_ATTEMPTS = 4;
const BASE_DELAY_MS = 400;
const MAX_DELAY_MS = 4000;

export type CardFacesStatus = "idle" | "loading" | "ready" | "failed";

type ManifestEntry = { file: string; name?: string };
type Attempt = (n: number) => Promise<CardFaces>;

let faces: CardFaces | null = null;
let status: CardFacesStatus = "idle";
let inFlight: Promise<CardFaces> | null = null;
const listeners = new Set<() => void>();

function setStatus(next: CardFacesStatus): void {
  status = next;
  for (const l of listeners) l();
}

export function cardFacesReady(): CardFaces | null {
  return faces;
}

export function cardFacesStatus(): CardFacesStatus {
  return status;
}

/** Subscribe to status changes (`useSyncExternalStore` contract). */
export function subscribeCardFaces(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The chunk's real URL from Vite's manifest.
 *
 * Looked up by source key, then — if a refactor moves the file — by scanning for the emitted
 * chunk's name. A silently-unresolvable URL would mean a retry path that cannot retry, which is
 * precisely the failure this module exists to avoid, so the fallback is deliberate.
 */
export function chunkUrlFromManifest(
  manifest: Record<string, ManifestEntry>,
  key: string = CHUNK_SOURCE,
): string | null {
  const direct = manifest[key];
  if (direct?.file) return `/${direct.file}`;
  const base = key
    .split("/")
    .pop()
    ?.replace(/\.[tj]sx?$/, "");
  for (const [k, entry] of Object.entries(manifest)) {
    if (!entry?.file) continue;
    if (k.endsWith(key) || (base && (entry.name === base || k.endsWith(`${base}.ts`)))) {
      return `/${entry.file}`;
    }
  }
  return null;
}

let cachedUrl: string | null | undefined;

async function resolveChunkUrl(): Promise<string | null> {
  if (cachedUrl !== undefined) return cachedUrl;
  try {
    const res = await fetch(MANIFEST_URL, { cache: "no-cache" });
    if (!res.ok) throw new Error(`manifest ${res.status}`);
    cachedUrl = chunkUrlFromManifest((await res.json()) as Record<string, ManifestEntry>);
  } catch {
    // Leave it UNCACHED on failure: the manifest is a tiny file on the same origin that was just
    // proven to work, so a transient miss should not permanently disable the retry path.
    return null;
  }
  return cachedUrl;
}

/** Attempt 0: the ordinary Vite dynamic import. Later attempts: a distinct URL (see the note above). */
const defaultAttempt: Attempt = async (n) => {
  if (n === 0) return (await import("./card-faces-chunk")).default;
  const url = await resolveChunkUrl();
  if (!url) throw new Error("card-faces: chunk url unresolved (no manifest entry)");
  const mod = (await import(/* @vite-ignore */ `${url}?retry=${n}`)) as { default: CardFaces };
  return mod.default;
};

let attempt: Attempt = defaultAttempt;
let sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Told when every attempt is spent — see {@link setCardFacesFailureSink}. */
export type CardFacesFailure = { chunk: string; attempts: number; lastError: unknown };

/**
 * Where a TERMINAL load failure is announced. Wired to the beacon in `main.tsx`.
 *
 * ⚠️ INJECTED, NOT IMPORTED, AND THAT IS DELIBERATE. Importing `client-report` here would drag
 * `telegram/launch` and the whole Telegram SDK into this module — which is meant to stay
 * framework-free — and because `test/setup.ts` imports THIS module to seed the artwork, that
 * import chain gets instantiated in every test file before its own `vi.mock` calls can register.
 * Trying it that way broke `launch-start-param` and `beacon-bundle`, two files with nothing to do
 * with card faces. A one-line sink keeps the loader's dependencies to none.
 */
let onTerminalFailure: (failure: CardFacesFailure) => void = () => undefined;

export function setCardFacesFailureSink(sink: (failure: CardFacesFailure) => void): void {
  onTerminalFailure = sink;
}

async function run(): Promise<CardFaces> {
  let lastError: unknown;
  for (let n = 0; n <= MAX_ATTEMPTS; n++) {
    try {
      const loaded = await attempt(n);
      faces = loaded;
      setStatus("ready");
      return loaded;
    } catch (err) {
      lastError = err;
      if (n === MAX_ATTEMPTS) break;
      const backoff = Math.min(BASE_DELAY_MS * 2 ** n, MAX_DELAY_MS);
      await sleep(backoff * (0.7 + Math.random() * 0.6));
    }
  }
  setStatus("failed");
  /**
   * ⚠️ SAY SO OUT LOUD — A DEAD CHUNK WAS COMPLETELY INVISIBLE UNTIL NOW.
   *
   * The capture-phase listener in `lifecycle.ts` catches a resource that fires an `error` EVENT on
   * its ELEMENT (`<img>`, `<link>`, `<script>`). A dynamic `import()` failure is a rejected
   * PROMISE — there is no element and no event, so that listener structurally cannot see it; and
   * every caller here deliberately `.catch()`es, so it never reaches `unhandledrejection` either.
   * The result was that the artwork could exhaust all five attempts, leave the player on a
   * recovery screen unable to open any card game, and report NOTHING. (`tag=script` has never
   * appeared in ErrorLog once. On 2026-08-04 a user's chunk was severed at 97,901 bytes and not a
   * single beacon fired — we only found it because a picker happened to throw.)
   *
   * Only the TERMINAL failure is reported: a severed transfer that the retry rescues is the normal
   * weather on these networks and is not worth waking anyone for.
   */
  onTerminalFailure({
    chunk: "card-faces",
    attempts: MAX_ATTEMPTS + 1,
    lastError: lastError instanceof Error ? lastError : String(lastError),
  });
  throw lastError instanceof Error ? lastError : new Error("card-faces: load failed");
}

/**
 * Load the faces, at most once at a time.
 *
 * Single-flight matters concretely: the after-boot prefetch and a game start can overlap, and two
 * parallel fetches of a 155 KB chunk on a throttled link is the exact concurrency that makes
 * transfers fail. On terminal failure the in-flight promise is CLEARED so a later call (the user
 * tapping «تلاش مجدد») starts a fresh round rather than re-receiving the cached rejection.
 */
export function loadCardFaces(): Promise<CardFaces> {
  if (faces) return Promise.resolve(faces);
  if (inFlight) return inFlight;
  setStatus("loading");
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Warm the chunk in the background.
 *
 * ⚠️ CALL THIS ONLY AFTER BOOT HAS FINISHED. The investigation that produced this module found
 * the 94.7 KB stylesheet failing 4.06% of the time while it downloaded ALONGSIDE the 1.38 MB
 * bundle, against 0.14% for a 69 KB asset fetched after boot. Prefetching during boot would
 * recreate exactly that contention and could make the boot fetch — the un-retryable one — worse.
 */
export function prefetchCardFaces(): void {
  void loadCardFaces().catch(() => undefined);
}

/** Test seam — inject the attempt sequence (and skip real backoff). */
export function __setCardFacesAttemptForTests(
  next: Attempt,
  fakeSleep?: (ms: number) => Promise<void>,
): void {
  attempt = next;
  if (fakeSleep) sleep = fakeSleep;
}

/** Test seam — seed the faces so table tests render real cards synchronously. */
export function __setCardFacesForTests(next: CardFaces | null): void {
  faces = next;
  status = next ? "ready" : "idle";
  inFlight = null;
}

export function __resetCardFacesForTests(): void {
  faces = null;
  status = "idle";
  inFlight = null;
  cachedUrl = undefined;
  attempt = defaultAttempt;
  sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  listeners.clear();
}
